import { useState, useMemo } from "react";
import ReactDataGrid from '@inovua/reactdatagrid-community';
import NumberFilter from '@inovua/reactdatagrid-community/NumberFilter';
import '@inovua/reactdatagrid-community/index.css';
import { Form } from "react-bootstrap";
import { sortProgrammesLikeOpleidingenPage } from "../../../_helpers/sortProgrammes";

const fmt = v => v != null ? parseFloat(v).toFixed(2) : '';

const clusterFilterValue = [
  { name: 'name', operator: 'contains', type: 'string', value: '' },
  { name: 'stp', operator: 'inrange', type: 'number', value: 0 },
  { name: 'budgetVTE', operator: 'inrange', type: 'number', value: 0 },
  { name: 'availableFTE', operator: 'inrange', type: 'number', value: 0 },
  { name: 'fte', operator: 'inrange', type: 'number', value: 0 },
];

const clusterColumns = [
  {
    name: 'name', header: 'Opleiding', defaultFlex: 1,
    render: ({ value, data }) => {
      if (data.isClusterRow) return <strong>{value}</strong>;
      if (data.isChild) return <span className="cluster-child-name">{value}</span>;
      return <span>{value}</span>;
    }
  },
  { name: 'stp', header: 'Credits', type: 'number', defaultFlex: 1, filterEditor: NumberFilter, textAlign: 'end', render: ({ value }) => value ? Math.round(value).toLocaleString('en-US') : '' },
  { name: 'budgetVTE', header: 'Budget VTE', type: 'number', defaultFlex: 1, filterEditor: NumberFilter, textAlign: 'end', render: ({ value }) => value != null ? value.toFixed(2) : '' },
  { name: 'availableFTE', header: 'Beschikbaar VTE', type: 'number', defaultFlex: 1, filterEditor: NumberFilter, textAlign: 'end', render: ({ value }) => fmt(value) },
  { name: 'fte', header: 'Totaal geplande VTE', type: 'number', defaultFlex: 1, filterEditor: NumberFilter, textAlign: 'end', render: ({ value }) => fmt(value) },
];

function ClusterDistributionGrid({ clusters, programmes, totalStp, variabelDeelOpleidingen, werBudgetTotal, unitTaskVTE, budgetSources, allocationMap }) {

  const getProgrammeTotaalPersoneel = (programmeID) => {
    if (!budgetSources || !allocationMap) return 0;
    return budgetSources.reduce((sum, src) => {
      const alloc = allocationMap.get(`${src.ID}-${programmeID}`);
      return sum + (alloc ? alloc.amountFTE : 0);
    }, 0);
  };
  const [showOnlyClusters, setShowOnlyClusters] = useState(false);

  const clusterGridData = useMemo(() => {
    const makeRow = (prog, isClusterRow, isChild) => {
      const stp = prog.credit || 0;

      const share = totalStp > 0 ? (stp / totalStp) * variabelDeelOpleidingen : 0;
      const children = isClusterRow
        ? programmes.filter(p => !p.isCluster && p.clusterProgrammeId === prog.ID)
        : [];

      let budgetVTE;
      if (prog.useAllocationAsBudgetVte) {
        budgetVTE = getProgrammeTotaalPersoneel(prog.ID);
      } else if (isClusterRow) {
        budgetVTE = share;
      } else {
        budgetVTE = stp > 0 ? share : (prog.fte || 0);
      }

      const fte = isClusterRow
        ? (prog.fte || 0) + children.reduce((sum, c) => sum + (c.fte || 0), 0)
        : prog.fte;

      return { ID: prog.ID, name: prog.name, isClusterRow, isChild, stp, budgetVTE, availableFTE: prog.availableFTE, fte };
    };

    return sortProgrammesLikeOpleidingenPage(programmes).map(p =>
      makeRow(p, !!p.isCluster, !p.isCluster && !!p.clusterProgrammeId)
    );
  }, [programmes, totalStp, variabelDeelOpleidingen, allocationMap, budgetSources]);

  const totalBudget = clusterGridData.filter(r => !r.isChild).reduce((s, r) => s + (r.budgetVTE || 0), 0);
  const totalAvailable = programmes.reduce((s, p) => s + (p.availableFTE || 0), 0);
  const totalPlanned = programmes.reduce((s, p) => s + (p.fte || 0), 0);

  const gridDataWithTotal = useMemo(() => [
    ...clusterGridData,
    {
      ID: '__total__',
      name: 'Totaal alle opleidingen',
      stp: Math.round(totalStp),
      budgetVTE: totalBudget,
      availableFTE: totalAvailable,
      fte: totalPlanned,
      isTotalRow: true,
    },
  ], [clusterGridData, totalStp, totalBudget, totalAvailable, totalPlanned]);

  return (
    <>
      <h3>Verdeling over clusters</h3>
      <p className="text-muted">
        Variabel deel voor opleidingen: <strong>{variabelDeelOpleidingen.toFixed(2)} VTE</strong>
      </p>
      <Form.Check
        type="switch"
        id="cluster-filter-switch"
        label="Verberg gekoppelde deelopleidingen"
        checked={showOnlyClusters}
        onChange={(e) => setShowOnlyClusters(e.target.checked)}
        className="mb-2"
      />
      <ReactDataGrid
        idProperty="ID"
        className="cluster-grid"
        defaultFilterValue={clusterFilterValue}
        columns={clusterColumns}
        dataSource={showOnlyClusters ? gridDataWithTotal.filter(r => !r.isChild) : gridDataWithTotal}
        rowHeight={40}
        rowStyle={({ data }) => data.isTotalRow ? { background: '#dee2e6', fontWeight: 'bold', borderTop: '1px solid #000', borderBottom: '2px solid #000' } : data.isClusterRow ? { background: '#e9ecef' } : { background: '#fff' }}
      />
    </>
  );
}

export default ClusterDistributionGrid;
